import { useState, useEffect, useCallback, useRef } from 'react';
import { VIDEO_CONFIG } from '@shared/utils/constants';
import { getSupportedMimeType, blobToBase64 } from '@shared/utils/helpers';
import { useInterview } from '@features/interview/context/InterviewContext';

export const useRecorder = (stream) => {
  const { state, actions } = useInterview();
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  // Cumulative byte offsets: chunkOffsetsRef[i] = total bytes BEFORE chunk i
  // This lets us find which MediaRecorder chunks cover a given byte range
  // without rebuilding the entire blob.
  const chunkOffsetsRef = useRef([]);
  const totalBytesRef = useRef(0);
  const chunkTimerRef = useRef(null);
  const durationTimerRef = useRef(null);
  
  const [recordingState, setRecordingState] = useState('inactive');
  const [mimeType, setMimeType] = useState(null);
  const [error, setError] = useState(null);

  // Initialize MIME type
  useEffect(() => {
    const supportedType = getSupportedMimeType();
    setMimeType(supportedType);
    
    if (!supportedType) {
      setError({
        type: 'support',
        message: 'No supported video recording format found',
      });
    }
  }, []);

  // Create MediaRecorder
  const createRecorder = useCallback(() => {
    if (!stream || !mimeType) {
      return null;
    }

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2500000, // 2.5 Mbps
        audioBitsPerSecond: 128000, // 128 kbps
      });

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunkOffsetsRef.current.push(totalBytesRef.current);
          totalBytesRef.current += event.data.size;
          chunksRef.current.push(event.data);
          actions.addVideoChunk(event.data);
        }
      };

      recorder.onstart = () => {
        setRecordingState('recording');
        actions.startRecording();
        
        // Start duration timer
        const startTime = Date.now();
        durationTimerRef.current = setInterval(() => {
          const duration = Math.floor((Date.now() - startTime) / 1000);
          actions.updateRecordingDuration(duration);
        }, 1000);
      };

      recorder.onstop = () => {
        setRecordingState('inactive');
        actions.stopRecording();
        
        // Clear timers
        if (durationTimerRef.current) {
          clearInterval(durationTimerRef.current);
          durationTimerRef.current = null;
        }
        if (chunkTimerRef.current) {
          clearInterval(chunkTimerRef.current);
          chunkTimerRef.current = null;
        }
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError({
          type: 'recording',
          message: event.error?.message || 'Recording error occurred',
        });
        setRecordingState('inactive');
      };

      recorder.onpause = () => {
        setRecordingState('paused');
      };

      recorder.onresume = () => {
        setRecordingState('recording');
      };

      return recorder;
    } catch (err) {
      console.error('Error creating MediaRecorder:', err);
      setError({
        type: 'initialization',
        message: err.message,
      });
      return null;
    }
  }, [stream, mimeType, actions]);

  // Start recording
  const startRecording = useCallback((options = {}) => {
    const { chunkInterval = VIDEO_CONFIG.CHUNK_INTERVAL } = options;
    
    if (recordingState === 'recording') {
      console.warn('Already recording');
      return false;
    }

    chunksRef.current = [];
    chunkOffsetsRef.current = [];
    totalBytesRef.current = 0;
    actions.clearVideoChunks();
    setError(null);

    const recorder = createRecorder();
    if (!recorder) {
      return false;
    }

    mediaRecorderRef.current = recorder;

    try {
      // Request data at intervals for chunked upload
      recorder.start(chunkInterval);
      
      // Setup periodic chunk collection for backend upload
      chunkTimerRef.current = setInterval(() => {
        if (recorder.state === 'recording') {
          recorder.requestData();
        }
      }, chunkInterval);

      return true;
    } catch (err) {
      console.error('Error starting recording:', err);
      setError({
        type: 'start',
        message: err.message,
      });
      return false;
    }
  }, [recordingState, createRecorder, actions]);

  // Stop recording
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || recordingState === 'inactive') {
        resolve(null);
        return;
      }

      const recorder = mediaRecorderRef.current;
      
      recorder.onstop = () => {
        setRecordingState('inactive');
        actions.stopRecording();
        
        // Clear timers
        if (durationTimerRef.current) {
          clearInterval(durationTimerRef.current);
        }
        if (chunkTimerRef.current) {
          clearInterval(chunkTimerRef.current);
        }

        // Create final blob
        const blob = new Blob(chunksRef.current, { type: mimeType });
        resolve({
          blob,
          chunks: chunksRef.current,
          duration: state.recordingDuration,
          mimeType,
        });
      };

      recorder.stop();
    });
  }, [recordingState, mimeType, state.recordingDuration, actions]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.pause();
      return true;
    }
    return false;
  }, [recordingState]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'paused') {
      mediaRecorderRef.current.resume();
      return true;
    }
    return false;
  }, [recordingState]);

  // Get current recording blob (used for final upload after stopRecording)
  const getCurrentBlob = useCallback(() => {
    if (chunksRef.current.length === 0) {
      return null;
    }
    return new Blob(chunksRef.current, { type: mimeType });
  }, [mimeType]);

  // Total recorded bytes so far (cheap — no blob construction)
  const getTotalBytes = useCallback(() => totalBytesRef.current, []);

  // Build a blob covering exactly [startByte, endByte) from the recorded
  // MediaRecorder chunks. Only the overlapping chunks are touched — this
  // avoids constructing the full recording blob.
  const sliceByteRange = useCallback((startByte, endByte) => {
    const offsets = chunkOffsetsRef.current;
    const chunks = chunksRef.current;
    if (chunks.length === 0 || startByte >= endByte) return null;

    // Binary-ish scan: find first chunk whose end > startByte
    const parts = [];
    for (let i = 0; i < chunks.length; i++) {
      const chunkStart = offsets[i];
      const chunkEnd = chunkStart + chunks[i].size;
      if (chunkEnd <= startByte) continue; // entirely before range
      if (chunkStart >= endByte) break;    // entirely after range

      // This chunk overlaps [startByte, endByte)
      const sliceStart = Math.max(0, startByte - chunkStart);
      const sliceEnd = Math.min(chunks[i].size, endByte - chunkStart);
      if (sliceStart === 0 && sliceEnd === chunks[i].size) {
        parts.push(chunks[i]); // use whole chunk (no copy)
      } else {
        parts.push(chunks[i].slice(sliceStart, sliceEnd));
      }
    }
    return parts.length > 0 ? new Blob(parts, { type: mimeType }) : null;
  }, [mimeType]);

  // Get current chunks for upload
  const getChunksForUpload = useCallback(async () => {
    const chunks = [...chunksRef.current];
    const chunkData = await Promise.all(
      chunks.map(async (chunk, index) => ({
        index,
        data: await blobToBase64(chunk),
        size: chunk.size,
        timestamp: Date.now(),
      }))
    );
    return chunkData;
  }, []);

  // Get latest chunk for streaming upload
  const getLatestChunk = useCallback(() => {
    if (chunksRef.current.length === 0) {
      return null;
    }
    return chunksRef.current[chunksRef.current.length - 1];
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
      if (chunkTimerRef.current) {
        clearInterval(chunkTimerRef.current);
      }
    };
  }, []);

  return {
    // State
    recordingState,
    isRecording: recordingState === 'recording',
    isPaused: recordingState === 'paused',
    duration: state.recordingDuration,
    chunks: chunksRef.current,
    chunkCount: chunksRef.current.length,
    mimeType,
    error,
    isSupported: !!mimeType,
    
    // Actions
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    getCurrentBlob,
    getTotalBytes,
    sliceByteRange,
    getChunksForUpload,
    getLatestChunk,
  };
};

export default useRecorder;
